import { useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { interviews } from "@/mocks/interviews";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useJsonLd } from "@/hooks/useJsonLd";

export default function InterviewsPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [featured, ...rest] = interviews;

  useSeoMeta({
    title: t("interviews.page.title"),
    description: t("interviews.page.desc"),
    image: featured.thumbnail,
    type: "website",
    keywords: "interview entrepreneur africain, podcast Afrique, Strive Masiyiwa, Fatoumata Bâ, Iddris Sandu, Wazi Stories",
  });

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Interviews d'entrepreneurs africains — Wazi Stories",
    "url": `${siteUrl}/interviews`,
    "numberOfItems": interviews.length,
    "itemListElement": interviews.map((iv, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "VideoObject",
        "name": iv.title,
        "description": iv.excerpt,
        "thumbnailUrl": iv.thumbnail,
        "uploadDate": "2026-01-01",
        "duration": `PT${iv.duration.replace(":", "M")}S`,
        "embedUrl": iv.videoUrl,
        "author": {
          "@type": "Person",
          "name": iv.guest,
          "jobTitle": iv.guestTitle
        }
      }
    }))
  }, "interviews");

  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />

      {/* Hero */}
      <div className="pt-16 md:pt-20 pb-8 md:pb-12 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/78b5656f91113328237036d0f6c83200.jpg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950/70 via-gray-950/80 to-gray-950"></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 pt-10 md:pt-12 text-center">
          <div className="flex items-center justify-center gap-2 mb-3 md:mb-4">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
            <span className="text-red-400 text-xs font-bold uppercase tracking-widest">{t("interviews.hero.badge")}</span>
          </div>
          <h1 className="text-2xl md:text-5xl font-bold text-white font-serif mb-2 md:mb-3">{t("section.interviews.title")}</h1>
          <p className="text-gray-400 text-sm md:text-base max-w-xl mx-auto">{t("interviews.hero.subtitle")}</p>

          {/* Stats */}
          <div className="flex items-center justify-center gap-6 md:gap-8 mt-6 md:mt-8 overflow-x-auto pb-1">
            {[
              { value: interviews.length, labelKey: "interviews.stats.total" },
              { value: `${Math.round(interviews.reduce((a, b) => a + b.views, 0) / 1000)}K`, labelKey: "interviews.stats.views" },
              { value: `${interviews.length}`, labelKey: "interviews.stats.countries" },
            ].map((s) => (
              <div key={s.labelKey} className="text-center flex-shrink-0">
                <p className="text-xl md:text-2xl font-bold text-orange-400">{s.value}</p>
                <p className="text-gray-500 text-xs mt-1 whitespace-nowrap">{t(s.labelKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 md:py-10">

        {/* Featured interview */}
        <div className="mb-8 md:mb-12">
          <div className="flex items-center gap-2 mb-4 md:mb-6">
            <i className="ri-star-fill text-orange-400"></i>
            <h2 className="text-white font-bold text-base md:text-lg">{t("interviews.featured.label")}</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
            {/* Video */}
            <div className="relative">
              {playingId === featured.id ? (
                <div className="aspect-video">
                  <iframe
                    src={`${featured.videoUrl.replace('youtube.com', 'youtube-nocookie.com')}?autoplay=1&rel=0`}
                    title={featured.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <div className="relative aspect-video cursor-pointer group" onClick={() => setPlayingId(featured.id)}>
                  <img src={featured.thumbnail} alt={featured.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 md:w-20 h-16 md:h-20 flex items-center justify-center rounded-full bg-orange-500 group-hover:bg-orange-400 group-hover:scale-110 transition-all duration-300">
                      <i className="ri-play-fill text-white text-3xl md:text-4xl ml-1"></i>
                    </div>
                  </div>
                  <div className="absolute top-3 md:top-4 left-3 md:left-4">
                    <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <i className="ri-live-line"></i>{t("interviews.hero.badge")}
                    </span>
                  </div>
                  <div className="absolute bottom-3 md:bottom-4 right-3 md:right-4 bg-black/70 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                    {featured.duration}
                  </div>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="p-4 md:p-6 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-3 md:mb-4">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-full overflow-hidden bg-gray-700 flex-shrink-0">
                  <img src={featured.guestPhoto} alt={featured.guest} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm md:text-base">{featured.guest}</p>
                  <p className="text-gray-400 text-xs md:text-sm">{featured.guestTitle}</p>
                </div>
              </div>
              <h2 className="text-white font-bold text-base md:text-xl leading-snug mb-2 md:mb-3">{featured.title}</h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-3 md:mb-4 line-clamp-3 md:line-clamp-none">{featured.excerpt}</p>
              <div className="flex flex-wrap gap-2 mb-3 md:mb-4">
                {featured.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-gray-800 text-gray-400 px-2.5 py-1 rounded-full">{tag}</span>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>{(featured.views / 1000).toFixed(0)}K {t("interviews.meta.views")}
                </span>
                <span className="flex items-center gap-1"><i className="ri-time-line"></i>{featured.duration}</span>
                <span>{featured.countryFlag} {featured.country}</span>
              </div>
            </div>
          </div>
        </div>

        {/* All interviews grid */}
        <h2 className="text-white font-bold text-base md:text-lg mb-4 md:mb-6">{t("interviews.all.label")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {rest.map((interview) => (
            <div
              key={interview.id}
              className="group bg-gray-900 rounded-2xl border border-gray-800 hover:border-orange-500/40 overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              onClick={() => setPlayingId(interview.id)}
            >
              <div className="relative aspect-video overflow-hidden">
                {playingId === interview.id ? (
                  <iframe
                    src={`${interview.videoUrl.replace('youtube.com', 'youtube-nocookie.com')}?autoplay=1&rel=0`}
                    title={interview.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <>
                    <img src={interview.thumbnail} alt={interview.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-orange-500/90 group-hover:bg-orange-400 group-hover:scale-110 transition-all duration-300">
                        <i className="ri-play-fill text-white text-xl ml-0.5"></i>
                      </div>
                    </div>
                    <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                      {interview.duration}
                    </div>
                  </>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-700 flex-shrink-0">
                    <img src={interview.guestPhoto} alt={interview.guest} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{interview.guest}</p>
                    <p className="text-gray-500 text-xs line-clamp-1">{interview.guestTitle}</p>
                  </div>
                </div>
                <h3 className="text-white font-bold text-sm leading-snug line-clamp-2 mb-2 group-hover:text-orange-300 transition-colors">{interview.title}</h3>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>{(interview.views / 1000).toFixed(0)}K
                  </span>
                  <span>{interview.countryFlag} {interview.sector}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
